import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditAccountSelectField } from '../../../EditAccountSelectField';
import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const LP_CONSULTANT_FIELDS: FieldMapping[] = [
  { apiField: 'lpConsultantGeneralId', formField: 'lpConsultantGeneralId' },
  { apiField: 'lpConsultantId', formField: 'lpConsultantPeId' },
  { apiField: 'isIcapitalPlatform', formField: 'iCapital' },
  {
    apiField: 'dateLpPlatformSuppressUnchecked',
    formField: 'dateLpPlatformSuppressUnchecked',
    isDate: true
  },
  {
    apiField: 'reasonForUncheckedLpPlatformSuppress',
    formField: 'reasonForUncheckedLpPlatformSuppress'
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      }
    }
  }

  return changes;
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        lpConsultantGeneralId {
          id
          name
        }
        lpConsultantId {
          id
          name
        }
        isIcapitalPlatform
        dateLpPlatformSuppressUnchecked
        reasonForUncheckedLpPlatformSuppress
      }
    }
  }
`;

export interface EditLpConsultantInfoDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditLpConsultantInfoDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditLpConsultantInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(LP_CONSULTANT_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save LP & Consultant info');
      } else {
        toast.success('LP & Consultant info saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving LP & Consultant info:', error);
      toast.error('Failed to save LP & Consultant info');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit LP & Consultant Info"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditAccountSelectField
          currentAccountId={accountId}
          label="LP Consultant (General)"
          onChange={(id, name) => {
            onChange({
              ...value,
              lpConsultantGeneral: name,
              lpConsultantGeneralId: id
            });
          }}
          value={
            value.lpConsultantGeneralId
              ? {
                  id: value.lpConsultantGeneralId,
                  name: value.lpConsultantGeneral
                }
              : null
          }
        />
        <EditAccountSelectField
          currentAccountId={accountId}
          label="LP Consultant (PE)"
          onChange={(id, name) => {
            onChange({
              ...value,
              lpConsultantPe: name,
              lpConsultantPeId: id
            });
          }}
          value={
            value.lpConsultantPeId
              ? { id: value.lpConsultantPeId, name: value.lpConsultantPe }
              : null
          }
        />
        <EditCheckboxField
          checked={value.iCapital}
          label="iCapital"
          onCheckedChange={iCapital => {
            updateField('iCapital', iCapital);
          }}
        />
        <EditFormField
          label="Date LP Platform Suppress Unchecked"
          onChange={v => {
            updateField('dateLpPlatformSuppressUnchecked', v);
          }}
          type="date"
          value={toDateInputValue(value.dateLpPlatformSuppressUnchecked)}
        />
        <EditFormField
          label="Reason for Unchecked LP Platform Suppress"
          onChange={v => {
            updateField('reasonForUncheckedLpPlatformSuppress', v);
          }}
          value={value.reasonForUncheckedLpPlatformSuppress}
        />
      </div>
    </EditDialog>
  );
}
